import { pluralize, set } from '../util';
import Form from '@jgile/laravel-vue/resources/js/forms/Form';
import QueryBuilder from '@jgile/laravel-vue/resources/js/lib/QueryBuilder';

/**
 * Base class for all things common between Model and Collection.
 */
class Base {
    /**
     * Resource errors.
     *
     * @returns {*}
     */
    get errors() {
        return this._form.errors;
    }

    /**
     * Resource is busy.
     *
     * @returns {boolean}
     */
    get busy() {
        return this._form.busy;
    }

    /**
     * Constructor
     *
     * @param attributes
     * @param options
     * @returns {*}
     */
    constructor(attributes, options) {
        // Define an automatic unique ID. This is primarily to distinguish
        // between multiple instances of the same name and data.
        Object.defineProperty(this, '_uid', {
            value: _.uniqueId(),
            enumerable: false,
            configurable: false,
            writable: false,
        });

        Vue.set(this, '_listeners', {});
        Vue.set(this, '_options', {});
        Vue.set(this, '_form', new Form(this));

        this.setOptions(options);
    }

    /**
     * Called after construction, this hook allows you to add some extra setup
     * logic without having to override the constructor.
     */
    boot() {}

    /**
     * Returns a route configuration in the form {key: name}, where key may be
     * 'save', 'fetch', 'delete' or any other custom key, and the name is what
     * will be passed to the route resolver to generate the URL. See @getURL
     *
     * @returns {Object}
     */
    routes() {
        return {
            get: `/api/{resource}`,
            show: `/api/{resource}/{identifier}`,
            create: `/api/{resource}`,
            update: `/api/{resource}/{identifier}`,
            delete: `/api/{resource}/{identifier}`,
        };
    }

    /**
     * @returns {Object} This class' default options.
     */
    getDefaultOptions() {
        return {
            identifier: 'id',
        };
    }

    /**
     * The resource name for a model.
     *
     * @returns {string}
     */
    resource() {
        return `${pluralize(2, _.kebabCase(this.constructor.name))}`;
    }

    get $resource() {
        return this.resource();
    }

    /**
     * Returns the default context for all events emitted by this instance.
     *
     * @returns {Object}
     */
    getDefaultEventContext() {
        return { target: this };
    }

    /**
     * Emits an event by name to all registered listeners on that event.
     * Listeners will be called in the order that they were added. If a listener
     * returns `false`, no other listeners will be called.
     *
     * @param {string} event    The name of the event to emit.
     * @param {Object} context  The context of the event, passed to listeners.
     */
    emit(event, context = {}) {
        let listeners = _.get(this._listeners, event);

        if (!listeners) {
            return;
        }

        // Create the context for the event.
        context = _.defaults({}, context, this.getDefaultEventContext());

        // Run through each listener. If any of them return false, stop the
        // iteration and mark that the event wasn't handled by all listeners.
        _.each(listeners, (listener) => listener(context));
    }

    /**
     * Registers an event listener for a given event.
     *
     * Event names can be comma-separated to register multiple events.
     *
     * @param {string}   event      The name of the event to listen for.
     * @param {function} listener   The event listener, accepts context.
     */
    on(event, listener) {
        let events = _.map(_.split(event, ','), _.trim);

        _.each(events, (event) => {
            this._listeners[event] = this._listeners[event] || [];
            this._listeners[event].push(listener);
        });
    }

    /**
     * @returns {boolean} whether this model is not persisted yet, ie. has not
     *                    been created yet. The default test is to check if the
     *                    model's identifier is missing.
     */
    isNew() {
        return _.isNil(this.identifier());
    }

    /**
     * @returns {boolean} the opposite of `isNew`, returns `true` if this model
     *                    is already persisted somewhere else.
     */
    isExisting() {
        return !this.isNew();
    }

    /**
     * Returns the model's identifier value.
     */
    identifier() {
        return this.getAttribute(this.getOption('identifier'));
    }

    /**
     * Validate model id.
     *
     * @param id
     * @returns {boolean}
     */
    isValidId(id) {
        return id !== undefined && id !== 0 && id !== '';
    }

    /**
     * @param {Array|string} path     Option path resolved by `get`
     * @param {*}            fallback Fallback value if the option is not set.
     *
     * @returns {*} The value of the given option path.
     */
    getOption(path, fallback = null) {
        return _.get(this._options, path, fallback);
    }

    /**
     * @returns {Object} This instance's default options.
     */
    options() {
        return {};
    }

    /**
     * Sets an option.
     *
     * @param {string} path
     * @param {*}      value
     */
    setOption(path, value) {
        set(this._options, path, value);
    }

    /**
     * Sets all given options. Successive values for the same option won't be
     * overwritten, so this follows the 'defaults' behaviour, and not 'merge'.
     *
     * @param {...Object} options One or more objects of options.
     */
    setOptions(options) {
        set(this, '_options', _.merge({}, this.getDefaultOptions(), options));
    }

    /**
     * Returns all the options that are currently set on this instance.
     *
     * @return {Object}
     */
    getOptions() {
        return _.defaultTo(this._options, {});
    }

    /**
     * Get route.
     *
     * @param key
     * @param replacements
     * @param attributes
     * @returns {*}
     */
    getRoute(key, replacements = {}, attributes = null) {
        let route = _.get(this.routes(), key);

        if (!route) {
            throw new Error(`Invalid or missing route`);
        }

        _.each(
            {
                id: this.identifier(),
                identifier: this.identifier(),
                resource: this.$resource,
            },
            (value, key) => {
                route = _.replace(route, `{${key}}`, value);
            },
        );

        if (attributes) {
            this.getBuilder().set(attributes);
        }

        if (this._builder) {
            route = `${route}?${this._builder.query()}`;
        }

        return route;
    }

    /**
     * Get the query builder.
     *
     * @returns {*}
     */
    getBuilder() {
        if (!this._builder) {
            set(this, '_builder', new QueryBuilder(this));
        }

        return this._builder;
    }

    query(query) {
        this.getBuilder().set(query);

        return this;
    }

    include(...args) {
        this.getBuilder().include(args);

        return this;
    }

    append(...args) {
        this.getBuilder().append(args);

        return this;
    }

    select(...fields) {
        this.getBuilder().select(...fields);

        return this;
    }

    where(field, value) {
        this.getBuilder().where(field, value);

        return this;
    }

    whereIn(field, array) {
        this.getBuilder().whereIn(field, array);

        return this;
    }

    orderBy(...args) {
        this.getBuilder().orderBy(...args);

        return this;
    }

    page(value) {
        this.getBuilder().page(value);

        return this;
    }

    limit(value) {
        this.getBuilder().limit(value);

        return this;
    }

    params(payload) {
        this.getBuilder().params(payload);

        return this;
    }

    presenter(payload) {
        this.getBuilder().presenter(payload);

        return this;
    }
}

export default Base;
